import * as Sentry from '@sentry/react';
import { billingAccountQuery, createBillingAccountMutation } from '../graphql-operations';
import { configActions } from '../reducers/config';
import { sessionActions } from '../reducers/session';
import * as types from '../types';
import { gqlTypes } from '../types';
import { identifyUser, lookupAndIdentifyUser } from './analytics/identifyUser';
import * as postie from './analytics/postie';
import {
  ApplePayAccountExistsError,
  BillingAccountCreationError,
  GenericApplePayError,
  StripeCheckoutAccountExistsError,
  ThreeDSecureRequiredError,
  UserExistsSignUpError,
  UserFacingError,
  logInternalError,
} from './errors';
import { isAxiosError, webApiClient } from './fi-api/apiUtils';
import client from './fi-api/client';
import store from './reduxStore';
import { clearUserSessionData } from './session';
import { getFiGrowthBook } from './growthbook';
import { Tenancy } from '../hooks/useTenancy';
import fiOneTrust from './oneTrust';
import i18next from '../../i18n';
import analytics from './analytics';
import { clearAnonymousIdCookie, getOrCreateAnonymousId } from './anonymousId';

export const MINIMUM_PASSWORD_LENGTH = 8;

export async function checkEmailExists(email: string): Promise<{ exists: boolean; hasActiveMembership?: boolean }> {
  const response = await webApiClient.post('/api/ecommerce/check-email', { email });
  return response.data;
}

async function initializeAndCreateSession(session: types.Session) {
  await createBillingAccountAndIdentifyUser(session);

  // We don't want to consider the user as "logged in" until we've successfully created a billing account for them.
  store.dispatch(sessionActions.createSession({ session }));
}

export async function login(email: string, password: string): Promise<types.Session> {
  if (!email || !password) {
    throw new UserFacingError(i18next.t('please_provide_both_an_email_and_password'));
  }

  try {
    const response = await webApiClient.post('/auth/login', {
      email,
      password,
    });

    const session: types.Session = {
      userId: response.data.userId,
      email: response.data.email,
      tenancy: response.data.tenancy,
      isRescuePartner: response.data.isRescuePartner,
      language: response.data.language,
      region: response.data.region,
    };

    await initializeAndCreateSession(session);
    store.dispatch(configActions.clearConfig());

    // update growthbook attributes — non-blocking so login isn't affected by growthbook issues
    await getFiGrowthBook()
      .setAttributes()
      .catch((err) => {
        logInternalError(err, { tags: { integration: 'growthbook' } });
      });

    fiOneTrust.syncUserConsentProfile(session.userId, response.data.consentJWT);

    return session;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 401) {
      throw new UserFacingError(i18next.t('invalid_email_or_password'));
    } else {
      logInternalError(error, {
        fingerprint: ['{{ default }}', error.message],
        tags: {
          checkoutStep: 'login',
        },
      });
      throw new Error(i18next.t('an_unknown_error_occurred'));
    }
  }
}

/**
 * Lightweight login that authenticates without clearing config or re-initializing
 * the app. Used by the unified checkout inline login to avoid remounting the page
 * and losing Stripe Elements state. The caller (useUnifiedPurchase.onLoginSuccess)
 * handles session dispatch and billing setup separately.
 */
export async function loginQuiet(email: string, password: string): Promise<types.Session> {
  if (!email || !password) {
    throw new UserFacingError(i18next.t('please_provide_both_an_email_and_password'));
  }

  try {
    const response = await webApiClient.post('/auth/login', {
      email,
      password,
    });

    return {
      userId: response.data.userId,
      email: response.data.email,
      tenancy: response.data.tenancy,
      isRescuePartner: response.data.isRescuePartner,
      language: response.data.language,
      region: response.data.region,
    };
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 401) {
      throw new UserFacingError(i18next.t('invalid_email_or_password'));
    } else {
      logInternalError(error, {
        fingerprint: ['{{ default }}', error.message],
        tags: {
          checkoutStep: 'loginQuiet',
        },
      });
      throw new Error(i18next.t('an_unknown_error_occurred'));
    }
  }
}

/**
 * The ecommerce app relies on session information in the Redux store to determine if the user is logged in.
 * It's possible that the user is already authenticated with tryfi.com but doesn't have a session data in the ecommerce
 * Redux store. An example is when the user is logged into nano.tryfi.com then comes to the store at shop.tryfi.com.
 *
 * This method will attempt to grab user information from a REST endpoint and store it in Redux. This endpoint will
 * return 401 if the user does not have a session cookie or has an invalid/expired session.
 */
export async function createSessionFromExistingCredentials(): Promise<types.Session | undefined> {
  try {
    // The /auth/currentuser endpoint will return session data if the user has a valid session cookie.
    // TODO: Add small number of retries for network errors?
    const response = await webApiClient.get('/auth/currentuser');
    // Preserve the impersonating flag from the existing redux session — /auth/currentuser
    // doesn't surface it today, so without this carry-over the banner would disappear after
    // the post-impersonate hard-refresh.
    const existingImpersonating = store.getState().session?.impersonating;
    const session: types.Session = {
      userId: response.data.userId,
      email: response.data.email,
      isRescuePartner: response.data.isRescuePartner,
      isVerizonCustomer: response.data.isVerizonCustomer,
      language: response.data.language,
      region: response.data.region,
      impersonating: existingImpersonating,
    };

    fiOneTrust.syncUserConsentProfile(session.userId, response.data.consentJWT);

    // If we're already set this same user id in the session, that means we've already gone through the session
    // and billing account initialization. But if any of the info has changed, we'll update the session and
    // re-initialize.
    const currentSession = store.getState().session;
    if (
      session.userId === currentSession?.userId &&
      session.email === currentSession?.email &&
      session.isRescuePartner === currentSession?.isRescuePartner &&
      session.isVerizonCustomer === currentSession?.isVerizonCustomer
    ) {
      // Decagon widget auth has to be re-applied on every page load — the widget is a fresh
      // instance each time and doesn't persist setUserIdAuth across refreshes. Do this even
      // when the session hasn't changed so the signature always lands on the current widget.
      lookupAndIdentifyUser(session).catch(() => {
        // Analytics only — safe to swallow.
      });
      return session;
    }

    await initializeAndCreateSession(session);

    return session;
  } catch (err) {
    // If we get a 401, it means the user's session has expired or does not have a session cookie in the first place,
    // so we should remove any expired session data from Redux and clear any other user-specific information we have
    // stored.
    if (isAxiosError(err) && err.response?.status === 401) {
      clearUserSessionData();
      return;
    }

    logInternalError(err, {
      fingerprint: ['{{ default }}', err.message],
      tags: {
        checkoutStep: 'createSession',
      },
    });
    throw err;
  }
}

export async function setPassword(newPassword: string) {
  await webApiClient.post('/auth/setpassword', { password: newPassword });
  store.dispatch(sessionActions.didSetPassword({}));
}

export async function logout() {
  try {
    await webApiClient.post('/auth/logout');
  } catch (error) {
    logInternalError(error, {
      fingerprint: ['{{ default }}', error.message],
      tags: {
        checkoutStep: 'logout',
      },
    });
    throw new Error(i18next.t('we_were_unable_to_log_you_out'));
  }

  // post-logout cleanup — none of these should prevent a successful logout
  clearUserSessionData();
  try {
    fiOneTrust.syncAnonymousConsentProfile();
  } catch {
    // non-critical
  }
  try {
    clearAnonymousIdCookie();
    analytics.reset();
    getOrCreateAnonymousId();
  } catch {
    // non-critical — analytics may not be fully initialized
  }
  Sentry.setUser(null);
}

/**
 * Signs up a new user. Password can be null for passwordless checkout - in that case,
 * the user will set their password later via password reset flow when they first log
 * into the mobile app.
 */
export async function signup(
  email: string,
  password: string | null,
  firstName: string,
  lastName: string,
  tenancy: Tenancy | undefined,
  recaptchaToken: string,
  passwordlessCheckout?: boolean,
  marketingEmailsOptedIn?: boolean,
): Promise<types.Session> {
  if (!email) {
    throw new UserFacingError(i18next.t('please_provide_your_email_address'));
  }
  if (!firstName || !lastName) {
    throw new UserFacingError(i18next.t('please_provide_your_first_and_last_name'));
  }

  let session: types.Session;
  try {
    const response = await webApiClient.post('/auth/signup', {
      email,
      password, // Can be null for passwordless checkout
      firstName,
      lastName,
      tenancy,
      recaptchaToken,
      passwordlessCheckout, // Allows testing before enabling flag globally
      marketingEmailsOptedIn,
    });

    postie.trackEmailSignup(email);
    identifyUser(response.data.userId, {
      email,
      name: { firstName, lastName },
      tenancy: response.data.tenancy,
      isRescuePartner: response.data.isRescuePartner,
    });

    session = {
      userId: response.data.userId,
      email,
      tenancy: response.data.tenancy,
      isRescuePartner: response.data.isRescuePartner,
      language: response.data.language,
      region: response.data.region,
    };
    fiOneTrust.syncUserConsentProfile(response.data.userId, response.data.consentJWT);
  } catch (error) {
    if (error.response.status === 409) {
      throw new UserExistsSignUpError();
    } else {
      logInternalError(error, {
        fingerprint: ['{{ default }}', error.message],
        tags: {
          checkoutStep: 'signup',
        },
      });
      throw new Error(i18next.t('an_unknown_error_occurred'));
    }
  }

  // Make sure to wait for the billing account here so that it's available
  // when the dispatch updates component props
  try {
    await createBillingAccountAndIdentifyUser(session);
  } catch (error) {
    logInternalError(error, {
      fingerprint: ['{{ default }}', error.message],
      tags: {
        checkoutStep: 'createBillingAccount',
      },
    });

    // If we were unable to create their billing account after signing up, remove any session cookies we have already
    // set because we're gonna want to have them login again which would retry creating their billing account.
    store.dispatch(sessionActions.destroySession());

    throw new BillingAccountCreationError();
  }

  store.dispatch(sessionActions.createSession({ session }));

  return session;
}

export async function sendPasswordResetEmail(email: string) {
  await webApiClient.post('/auth/sendpasswordreset', { email });
}

/**
 * Admin-only: log in as another user. Backend gates on `principal.isAdmin()` and rotates
 * the session id, which the response interceptor in apiUtils.ts persists to the parent-domain
 * cookie + localStorage. Caller is expected to hard-refresh the page so apollo + redux state
 * reset cleanly to the impersonated user.
 */
export async function impersonate(email: string): Promise<void> {
  const response = await webApiClient.post('/auth/impersonate', { email });
  const session: types.Session = {
    userId: response.data.userId,
    email: response.data.email ?? email,
    impersonating: true,
  };
  store.dispatch(sessionActions.createSession({ session }));
}

async function createBillingAccountAndIdentifyUser(session: types.Session): Promise<void> {
  /**
   * NOTE: We're not catching errors within this method to allow for callers to handle them in a more specific way.
   * Sign up needs to handle errors here differently than login, for example.
   */
  const billingAccount = await client.query<gqlTypes.billingAccount>({
    query: billingAccountQuery,
    fetchPolicy: 'network-only',
  });
  if (billingAccount.data.currentUser.billingAccount) {
    identifyUser(session.userId, {
      email: session.email,
      billingInfo: billingAccount.data.currentUser.billingAccount?.billingInfo,
      shippingAddress: billingAccount.data.currentUser.billingAccount?.address,
      tenancy: session.tenancy,
      isRescuePartner: billingAccount.data.currentUser.isRescuePartner,
      isVerizonCustomer: billingAccount.data.currentUser.isVerizonCustomer,
      decagonWidgetAuth: billingAccount.data.currentUser.decagonWidgetAuth,
    });
  } else {
    // If they don't already have a billing account we must create one and since we know they won't
    // have any billing info, we can just identify them with the session data we have.
    await client.mutate<gqlTypes.ECOMMERCE_createUserBillingAccount['createBillingAccount']>({
      mutation: createBillingAccountMutation,
    });
    identifyUser(session.userId, {
      email: session.email,
      tenancy: session.tenancy,
      isRescuePartner: session.isRescuePartner,
    });
  }
}

function applePayContactToParams(contact: ApplePayJS.ApplePayPaymentContact, country: string) {
  return {
    email: contact.emailAddress,
    firstName: contact.givenName,
    lastName: contact.familyName,
    address1: contact.addressLines && contact.addressLines[0],
    address2: contact.addressLines && contact.addressLines[1],
    city: contact.locality,
    state: contact.administrativeArea,
    phone: contact.phoneNumber,
    zip: contact.postalCode,
    country: (contact.countryCode && contact.countryCode.toUpperCase()) || country,
  };
}

export async function applePay(
  token: string,
  shippingAddressDetails: ApplePayJS.ApplePayPaymentContact,
  tenancy: string | undefined,
  country: string,
): Promise<types.Session> {
  try {
    const flattenedParams = applePayContactToParams(shippingAddressDetails, country);
    const response = await webApiClient.post('/api/ecommerce/applepay', {
      token,
      tenancy,
      ...flattenedParams,
    });
    const email = shippingAddressDetails.emailAddress;
    const session: types.Session = {
      userId: response.data.userId,
      noPassword: !response.data.hasPassword,
      email,
      tenancy: response.data.tenancy,
      isRescuePartner: response.data.isRescuePartner,
      isVerizonCustomer: response.data.isVerizonCustomer,
      language: response.data.language,
      region: response.data.region,
    };
    if (email) {
      postie.trackEmailSignup(email);
    }

    identifyUser(response.data.userId, {
      email,
      name: {
        firstName: flattenedParams.firstName,
        lastName: flattenedParams.lastName,
      },
      shippingAddress: {
        line1: flattenedParams.address1,
        line2: flattenedParams.address2,
        city: flattenedParams.city,
        state: flattenedParams.state,
        zip: flattenedParams.zip,
        country: flattenedParams.country,
        phone: flattenedParams.phone,
      },
      tenancy: session.tenancy,
      isRescuePartner: session.isRescuePartner,
      isVerizonCustomer: session.isVerizonCustomer,
    });

    // Don't create the session just yet. It can cause the cart page to re-render,
    // triggering another pricing calculation. Doing that in parallel with
    // the purchase event causes problems at Recurly with unique coupon codes.
    return session;
  } catch (error) {
    if (error.response.status === 409) {
      throw new ApplePayAccountExistsError();
    } else if (
      isAxiosError(error) &&
      error.response?.status === 400 &&
      error.response.data?.error?.code === 'customer_error' &&
      error.response.data?.error?.message
    ) {
      throw new UserFacingError(error.response.data.error.message);
    }

    logInternalError(error, {
      fingerprint: ['{{ default }}', error.message],
      tags: {
        paymentMethod: 'applePay',
        checkoutStep: 'updateBillingInfo',
      },
    });
    throw new GenericApplePayError();
  }
}

export interface StripeCheckoutParams {
  confirmationToken: string;
  email: string;
  firstName: string;
  lastName: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  phone: string;
  zip: string;
  country: string;
  tenancy: string | undefined;
  threeDSecureActionResultToken?: string;
  expressPaymentType?: string;
}

/**
 * Single endpoint call for Stripe Elements checkout. Creates account (if new user),
 * saves shipping address, and saves billing info with the Stripe confirmation token
 * in one request. Mirrors the Apple Pay endpoint pattern.
 */
export async function stripeCheckout(params: StripeCheckoutParams): Promise<types.Session> {
  try {
    const response = await webApiClient.post('/api/ecommerce/stripe-checkout', params);
    const session: types.Session = {
      userId: response.data.userId,
      noPassword: !response.data.hasPassword,
      email: params.email,
      tenancy: response.data.tenancy,
      isRescuePartner: response.data.isRescuePartner,
      isVerizonCustomer: response.data.isVerizonCustomer,
      language: response.data.language,
      region: response.data.region,
    };

    postie.trackEmailSignup(params.email);
    identifyUser(response.data.userId, {
      email: params.email,
      name: { firstName: params.firstName, lastName: params.lastName },
      shippingAddress: {
        line1: params.address1,
        line2: params.address2,
        city: params.city,
        state: params.state,
        zip: params.zip,
        country: params.country,
        phone: params.phone,
      },
      tenancy: session.tenancy,
      isRescuePartner: session.isRescuePartner,
      isVerizonCustomer: session.isVerizonCustomer,
    });

    return session;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 409) {
      throw new StripeCheckoutAccountExistsError(!!error.response.data?.error?.hasActiveMembership);
    }

    if (
      isAxiosError(error) &&
      error.response?.status === 400 &&
      error.response.data?.error?.code === 'customer_error' &&
      error.response.data?.error?.message
    ) {
      const threeDSToken = error.response.data.error.token;
      if (threeDSToken) {
        throw new ThreeDSecureRequiredError(error.response.data.error.message, threeDSToken);
      }
      throw new UserFacingError(error.response.data.error.message);
    }

    logInternalError(error, {
      fingerprint: ['{{ default }}', error instanceof Error ? error.message : 'unknown'],
      tags: {
        paymentMethod: params.expressPaymentType ?? 'stripeCheckout',
        checkoutStep: 'stripeCheckoutEndpoint',
      },
    });
    throw new UserFacingError(i18next.t('something_went_wrong'));
  }
}
